/**
 * Pulling records from a connected system — the surface for local sync (**ADR-074**).
 *
 * ⚠️ **Without this, `runSync` joins the list.** `ingest()` had 319 lines, tests, and no caller;
 * `runSync` gave it one, and a run nothing can start is the same failure one layer up. This is the
 * button.
 *
 * ── What it says, and why the wording is load-bearing ──────────────────────────────────────
 *
 *   • **No provider is connectable yet** ([B-130](../../../../docs/platform/TODO.md)) — Salesforce
 *     and HubSpot each need an OAuth app whose client secret lives in the relay's secret store. The
 *     panel says so in those words rather than showing a button that fails with a provider error.
 *   • **There is no schedule, and there will not be one here.** A browser tab is not a runtime we
 *     may promise anything about ([11 §6](../../../../docs/platform/11-technical-architecture.md)),
 *     so the panel states that the next pull is started by a person too — instead of implying a
 *     background job that does not exist.
 *   • **A resume is offered, never taken silently.** Continuing a pull someone started last week
 *     spends *their* API quota (BR-7); that is a decision to put in front of them.
 *
 * ── ⚠️ What changed on 2026-08-02 (Y-4, Y-5) ────────────────────────────────────────────────
 *
 *   • **The object is chosen, not assumed.** This took `provider.objects[0]`, so a Salesforce
 *     connection pulled contacts and silently never pulled accounts — while the screen said "pull
 *     records from a connected system", which is a wider promise than it kept. There is now a picker
 *     per connection, and **each object is its own source with its own checkpoint**, so pulling
 *     accounts cannot disturb a contacts pull that is mid-flight.
 *   • **Every pull is recorded.** There was only the current checkpoint, so "when did I last pull and
 *     what happened" became unanswerable the moment the next pull started, and a refused pull left no
 *     trace at all once someone retried it. `runSync` now appends a `sync_run` row on every path out,
 *     including the refusals — written there rather than here, because a history the caller has to
 *     remember to append to is complete for exactly one caller.
 */
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Callout, Card, HelpText, ProgressBar } from '@dedupr/ui';
import { accessToken } from '../auth.js';
import { openLocalStore, type StoredSyncRun } from '../local/store.js';
import type { IngestCheckpoint, ObjectDescriptor } from '@dedupr/connectors';
import { providersFor, runSync, type SyncTarget } from '../local/sync.js';

interface ConnectionView {
  id: string;
  provider: string;
  connectedAt: number;
  needsReconnect: boolean;
}

interface Progress {
  written: number;
  page: number;
}

const COUNT = new Intl.NumberFormat();

export function SyncPanel() {
  const [connections, setConnections] = useState<ConnectionView[] | null>(null);
  const [connectable, setConnectable] = useState<Set<string>>(new Set());
  const [checkpoints, setCheckpoints] = useState<Map<string, IngestCheckpoint>>(new Map());
  /** What each provider can be asked for, so the picker offers exactly that and nothing invented. */
  const [objects, setObjects] = useState<Map<string, ObjectDescriptor[]>>(new Map());
  /** Which object is selected per connection. Keyed by connection, because two connections to the
      same provider are two independent choices. */
  const [chosen, setChosen] = useState<Map<string, string>>(new Map());
  const [history, setHistory] = useState<StoredSyncRun[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [outcome, setOutcome] = useState<{ tone: 'success' | 'warning'; title: string; detail: string } | null>(null);

  const load = useCallback(async (alive: () => boolean = () => true) => {
    try {
      const token = await accessToken();
      const response = await fetch('/api/relay/connections', {
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      });
      const body = (await response.json()) as { connections?: ConnectionView[] };
      if (!alive()) return;
      setConnections(body.connections ?? []);
    } catch {
      if (!alive()) return;
      setConnections([]);
    }
    const providers = await providersFor();
    if (!alive()) return;
    setConnectable(new Set(providers.filter((p) => p.connectable).map((p) => p.id)));
    setObjects(new Map(providers.map((p) => [p.id, p.objects])));

    /* ⚠️ **Guarded, because IndexedDB can be refused outright** — Safari private browsing and
       Firefox's "never remember history" both do. `store.ts` is explicit that this is NOT a
       capability gate: a refused store still leaves a working product, so the panel degrades to
       "no resume on offer" rather than throwing. The first version let the rejection escape, which
       surfaced as an unhandled error in any environment without IndexedDB. */
    try {
      const store = await openLocalStore();
      try {
        const found = new Map<string, IngestCheckpoint>();
        for (const source of await store.listSources()) {
          const checkpoint = await store.readCheckpoint(source.id);
          /* Only an UNFINISHED one is an offer to resume. A completed checkpoint is the record of
             the last full pass, and presenting it as "resume" would invite re-running a done job. */
          if (checkpoint && !checkpoint.done) found.set(source.id, checkpoint);
        }
        const runs = await store.listSyncRuns();
        if (!alive()) return;
        setCheckpoints(found);
        setHistory(runs);
      } finally {
        store.close();
      }
    } catch {
      if (!alive()) return;
      setCheckpoints(new Map());
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    /* ⚠️ **Cancelled on unmount, and this is a real defect rather than test hygiene.** `load()` awaits
       the relay and then IndexedDB, so a component unmounted in between resolves into `setState` on a
       dead tree — which surfaced as an unhandled `ReferenceError: window is not defined` after a test
       environment had been torn down, and which Vitest itself warns "might cause false positive
       tests". In a browser it is the same shape: a navigation away mid-load leaves a promise writing
       into nothing. `mounted` is checked at every write rather than only at the top, because the two
       awaits are far apart. */
    let mounted = true;
    void load(() => mounted);
    return () => {
      mounted = false;
    };
  }, [load]);

  const pull = useCallback(
    async (connection: ConnectionView, objectId: string, resume: boolean) => {
      const providers = await providersFor();
      const provider = providers.find((p) => p.id === connection.provider);
      /* ⚠️ The chosen object, not `objects[0]`. The first version pulled whichever object the
         descriptor happened to declare first, so a Salesforce connection synced contacts and never
         accounts — with nothing on screen saying so. */
      const object = provider?.objects.find((o) => o.id === objectId);
      if (!provider || !object) return;

      const target: SyncTarget = {
        connectionId: connection.id,
        providerId: connection.provider as SyncTarget['providerId'],
        objectId: object.id,
        /* One source per (connection, object) — stable, so a second pull updates the same source
           rather than creating a new one beside it every time. */
        sourceId: `conn:${connection.id}:${object.id}`,
        displayName: `${provider.displayName} ${object.displayName.toLowerCase()}`,
      };

      setRunning(connection.id);
      setProgress({ written: 0, page: 0 });
      setOutcome(null);

      let store;
      try {
        store = await openLocalStore();
      } catch {
        /* Named rather than generic: "this browser will not let us store anything" is a different
           problem from a refused pull, and only one of them is fixable by trying again. */
        setRunning(null);
        setProgress(null);
        setOutcome({
          tone: 'warning',
          title: 'This browser will not let us store records',
          detail:
            'Pulling needs somewhere to put the records, and this browser is refusing local storage — private browsing usually does. Nothing was pulled.',
        });
        return;
      }

      try {
        const result = await runSync(store, {
          target,
          accessToken,
          resume,
          onProgress: (written, page) => setProgress({ written, page }),
        });
        if (result.ok) {
          const { written, reason, unblockable } = result.result;
          setOutcome({
            tone: reason === 'complete' ? 'success' : 'warning',
            title:
              reason === 'complete'
                ? /* ⚠️ **Zero is its own sentence — B-309.** An incremental pull that finds nothing
                     changed is the ORDINARY successful outcome, and it rendered as *"0 records pulled
                     · They are on this device now, ready to match"* — a success message about
                     nothing, whose plain reading is *"we looked in your CRM and there was nothing
                     there"*. The distinction this product exists to keep is between *nothing changed*
                     and *nothing found*, and the copy collapsed it. */
                  written === 0
                  ? 'Nothing has changed since the last pull'
                  : `${COUNT.format(written)} ${written === 1 ? 'record' : 'records'} pulled`
                : `${COUNT.format(written)} pulled, and there are more`,
            detail:
              reason === 'complete'
                ? written === 0
                  ? 'Every record we already hold is still current, so there was nothing new to bring across. What is on this device is unchanged.'
                  : unblockable > 0
                  ? `${COUNT.format(unblockable)} of them have no email, phone or name, so nothing can match them. That is not the same as having no duplicates.`
                  : 'They are on this device now, ready to match.'
                : /* Named honestly rather than as "done": `hit_record_cap` and `hit_source_ceiling`
                     are different problems with different remedies, and both are partial pulls. */
                  reason === 'hit_record_cap'
                  ? 'This build stops at its record cap. What arrived is kept, and you can continue the pull.'
                  : 'The system stopped returning results before the end. What arrived is kept, and you can continue the pull.',
          });
        } else {
          setOutcome({ tone: 'warning', title: 'That pull was refused', detail: result.detail });
        }
      } finally {
        store.close();
        setRunning(null);
        setProgress(null);
        await load();
      }
    },
    [load]
  );

  if (connections === null) return null;

  return (
    <Card id="sources-sync" title="Pull from a connected system" headingLevel={2}>
      {outcome ? (
        <Callout tone={outcome.tone} title={outcome.title}>
          {outcome.detail}
        </Callout>
      ) : null}

      {connections.length === 0 ? (
        <HelpText>
          Nothing is connected yet. Connect a system below and you can pull records from it here —
          they land on this device, exactly like a loaded file.
        </HelpText>
      ) : (
        <ul className="web-sources__list">
          {connections.map((connection) => {
            const available = objects.get(connection.provider) ?? [];
            const objectId = chosen.get(connection.id) ?? available[0]?.id ?? '';
            /* ⚠️ Per (connection, object). Each object is its own source with its own checkpoint, so
               an interrupted contacts pull is not reported against accounts — and pulling accounts
               cannot clear the offer to resume contacts. */
            const unfinished = checkpoints.get(`conn:${connection.id}:${objectId}`);
            const usable = connectable.has(connection.provider) && !connection.needsReconnect;
            return (
              <li key={connection.id} className="web-sources__row">
                <div className="web-sources__rowHead">
                  <div>
                    <p className="web-sources__name">{connection.provider}</p>
                    <p className="web-sources__meta">
                      {unfinished
                        ? `Interrupted at ${COUNT.format(unfinished.written)} ${unfinished.written === 1 ? 'record' : 'records'}`
                        : 'No pull in progress'}
                    </p>
                  </div>
                  <div className="web-sources__rowActions">
                    {connection.needsReconnect ? <Badge variant="warning">Reconnect first</Badge> : null}
                    {available.length > 1 ? (
                      <label className="web-sources__objectPick">
                        <span>What to pull</span>
                        <select
                          value={objectId}
                          disabled={running !== null}
                          onChange={(event) =>
                            setChosen((previous) =>
                              new Map(previous).set(connection.id, event.target.value)
                            )
                          }
                        >
                          {available.map((object) => (
                            <option key={object.id} value={object.id}>
                              {object.displayName}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {unfinished ? (
                      <Button
                        variant="secondary"
                        disabled={running !== null || !usable}
                        onClick={() => void pull(connection, objectId, true)}
                      >
                        Continue the pull
                      </Button>
                    ) : null}
                    <Button
                      disabled={running !== null || !usable || !objectId}
                      onClick={() => void pull(connection, objectId, false)}
                    >
                      {running === connection.id ? 'Pulling…' : unfinished ? 'Start again' : 'Pull records'}
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {running && progress ? (
        <>
          {/* Indeterminate on purpose: the total is not knowable until the last page, and a
              percentage derived from a guess is a number that goes backwards. */}
          <ProgressBar label="Pulling records" />
          <HelpText>
            {COUNT.format(progress.written)} {progress.written === 1 ? 'record' : 'records'} so far,
            page {COUNT.format(progress.page)}.
          </HelpText>
        </>
      ) : null}

      {/* ⚠️ Both sentences are the honest ones, and neither is a tier boundary dressed up. */}
      {connections.length > 0 && connectable.size === 0 ? (
        <Callout tone="info" title="No system can be pulled from yet">
          Pulling needs an OAuth app registered with that provider, whose secret lives in the
          connector service. None exists yet, in any mode — so a connection can be made and not yet
          read from.
        </Callout>
      ) : null}

      <HelpText>
        A pull runs while this screen is open, and stops when you close it — a browser tab is not
        something we can promise runs in the background, so there is no schedule here. What arrived
        is kept, and the next pull only asks for what changed since the last complete one.
        {objects.size > 0 ? ' Each thing you pull is kept as its own source, so pulling accounts does not disturb a contacts pull.' : ''}
      </HelpText>

      <SyncHistory runs={history} />
    </Card>
  );
}

/**
 * What has been pulled, when, and how each attempt ended.
 *
 * ⚠️ **Refusals are listed too, and that is the point.** Before this there was only the current
 * checkpoint, so a refused pull left no trace once somebody retried — which is precisely the run a
 * person needs to look back at. `refused`, `partial` and `complete` stay apart because they call for
 * different responses: a reconnect or a scope, a continuation, or nothing.
 */
function SyncHistory({ runs }: { runs: readonly StoredSyncRun[] }) {
  if (runs.length === 0) {
    /* Stated rather than omitted: an absent panel reads as "this screen does not do that", and a
       person who has pulled nothing yet should see where it will appear. */
    return (
      <HelpText id="sources-sync-history" tabIndex={-1}>
        No pull has run on this device yet. Once one has, what it pulled and how it ended is listed
        here — including a pull that was refused, which is the one worth looking back at.
      </HelpText>
    );
  }

  return (
    <div className="web-sources__history" id="sources-sync-history" tabIndex={-1}>
      <h3 className="web-sources__historyTitle">Recent pulls</h3>
      <ul className="web-sources__historyList">
        {runs.slice(0, 10).map((run) => (
          <li key={run.id}>
            <span className="web-sources__historyWhen">
              {new Date(run.finished_at).toLocaleString()}
            </span>
            <Badge
              variant={
                run.outcome === 'complete' ? 'success' : run.outcome === 'partial' ? 'neutral' : 'warning'
              }
            >
              {run.outcome === 'complete' ? 'Complete' : run.outcome === 'partial' ? 'Partial' : 'Refused'}
            </Badge>
            <span className="web-sources__historyWhat">
              {run.provider} {run.object_id}
              {run.resumed ? ' · continued' : ''}
            </span>
            <span className="web-sources__historyCount">
              {run.outcome === 'refused'
                ? /* The relay's own code, not "failed": `write_scope_missing` and `rate_limited` need
                     different responses, and one word for both hides which. */
                  run.reason
                : `${COUNT.format(run.written)} ${run.written === 1 ? 'record' : 'records'}`}
              {run.unblockable > 0 ? ` · ${COUNT.format(run.unblockable)} unmatchable` : ''}
            </span>
          </li>
        ))}
      </ul>
      {runs.length > 10 ? (
        <HelpText>
          Showing the 10 most recent of {COUNT.format(runs.length)}. Older ones are kept on this
          device.
        </HelpText>
      ) : null}
    </div>
  );
}
